(function () {
  'use strict';

  const DEBOUNCE_MS = 250;
  const MIN_QUERY_LENGTH = 2;

  function debounce(fn, delay) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function buildSuggestUrl(query) {
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,collection,page',
      'resources[limit]': '8',
      'resources[options][unavailable_products]': 'hide',
    });
    return `${window.Shopify?.routes?.predictive_search_url || '/search/suggest'}.json?${params.toString()}`;
  }

  function renderResults(container, data, query) {
    const resources = data?.resources?.results || {};
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];
    const items = [
      ...products.map((item) => ({ ...item, type: 'product' })),
      ...collections.map((item) => ({ ...item, type: 'collection' })),
      ...pages.map((item) => ({ ...item, type: 'page' })),
    ];

    container.innerHTML = '';

    if (!items.length) {
      container.hidden = true;
      return 0;
    }

    const list = document.createElement('ul');
    list.className = 'header-search__results-list';
    list.setAttribute('role', 'listbox');

    items.forEach((item, index) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'presentation');

      const link = document.createElement('a');
      link.className = 'header-search__result-link';
      link.href = item.url;
      link.setAttribute('role', 'option');
      link.id = `search-result-${index}`;
      link.dataset.searchResult = 'true';

      if (item.image) {
        const image = document.createElement('img');
        image.className = 'header-search__result-image';
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        link.appendChild(image);
      }

      const meta = document.createElement('span');
      meta.className = 'header-search__result-meta';

      const type = document.createElement('span');
      type.className = 'header-search__result-type';
      type.textContent = item.type;

      const title = document.createElement('span');
      title.textContent = item.title;

      meta.appendChild(type);
      meta.appendChild(title);
      link.appendChild(meta);
      li.appendChild(link);
      list.appendChild(li);
    });

    container.appendChild(list);
    container.hidden = false;
    return items.length;
  }

  class HeaderSearchController {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-search-modal]');
      const context = this.modal || root;
      this.input = context.querySelector('[data-search-input]');
      this.results = context.querySelector('[data-search-results]');
      this.status = context.querySelector('[data-search-status]');
      this.openButton = root.querySelector('[data-search-open]');
      this.closeButtons = this.modal
        ? this.modal.querySelectorAll('[data-search-close]')
        : root.querySelectorAll('[data-search-close]');
      this.activeIndex = -1;
      this.fetchResults = debounce(this.fetchResults.bind(this), DEBOUNCE_MS);

      this.bindEvents();
    }

    bindEvents() {
      this.input?.addEventListener('input', () => {
        const query = this.input.value.trim();
        if (query.length < MIN_QUERY_LENGTH) {
          this.clearResults();
          return;
        }
        this.fetchResults(query);
      });

      this.input?.addEventListener('keydown', (event) => this.onInputKeydown(event));

      this.openButton?.addEventListener('click', () => {
        if (!this.modal) return;
        this.modal.showModal();
        this.modal.querySelector('[data-search-input]')?.focus();
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      this.modal?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeModal();
      });

      document.addEventListener('click', (event) => {
        if (!this.root.contains(event.target)) {
          this.clearResults();
        }
      });
    }

    async fetchResults(query) {
      try {
        const response = await fetch(buildSuggestUrl(query));
        if (!response.ok) throw new Error('Predictive search failed');
        const data = await response.json();
        const count = renderResults(this.results, data, query);
        this.input?.setAttribute('aria-expanded', count > 0 ? 'true' : 'false');
        if (this.status) {
          const resultsText = this.status.dataset.resultsText;
          this.status.textContent = count
            ? (resultsText ? resultsText.replace('[count]', count).replace('[terms]', query) : `${count} results for ${query}`)
            : '';
        }
        this.activeIndex = -1;
      } catch (error) {
        console.error('[header-search]', error);
      }
    }

    clearResults() {
      if (this.results) {
        this.results.innerHTML = '';
        this.results.hidden = true;
      }
      this.input?.setAttribute('aria-expanded', 'false');
      if (this.status) this.status.textContent = '';
      this.activeIndex = -1;
    }

    closeModal() {
      this.modal?.close();
      this.clearResults();
      this.openButton?.focus();
    }

    onInputKeydown(event) {
      const options = this.results?.querySelectorAll('[data-search-result]');
      if (!options?.length) {
        if (event.key === 'Escape') this.closeModal();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
        this.highlightOption(options);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.highlightOption(options);
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        options[this.activeIndex].click();
      } else if (event.key === 'Escape') {
        this.clearResults();
        this.closeModal();
      }
    }

    highlightOption(options) {
      options.forEach((option, index) => {
        option.classList.toggle('is-active', index === this.activeIndex);
        if (index === this.activeIndex) {
          this.input?.setAttribute('aria-activedescendant', option.id);
        }
      });
    }
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-header-search]').forEach((root) => {
      if (root.dataset.searchInit === 'true') return;
      root.dataset.searchInit = 'true';
      new HeaderSearchController(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
